import { useState } from "react";
import { ArrowRight, MapPin, Navigation } from "lucide-react";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { TextField } from "../../components/Field";
import { api, ApiError } from "../../api/client";
import { track } from "../../analytics/analytics";
import { useSession } from "../../store/sessionStore";
import type { StepProps } from "../stepTypes";
import s from "../steps.module.css";

const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

/** Step 3 — postcode or "use my location"; resolves nearby branches. */
export function LocationStep({ onNext, saveLead }: StepProps) {
  const config = useSession((st) => st.config)!;
  const garageId = useSession((st) => st.garageId)!;
  const existing = useSession((st) => st.postcode);
  const setPostcode = useSession((st) => st.setPostcode);
  const setBranches = useSession((st) => st.setBranches);
  const branchId = useSession((st) => st.branchId);
  const setBranchId = useSession((st) => st.setBranchId);
  const registration = useSession((st) => st.vehicleRegistration);
  const setVehicle = useSession((st) => st.setVehicle);
  const setVehicleRegistration = useSession((st) => st.setVehicleRegistration);
  const setAvailableServices = useSession((st) => st.setAvailableServices);

  const [postcode, setLocal] = useState(existing ?? "");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = UK_POSTCODE.test(postcode.trim());

  async function continueWithGarageHive() {
    if (!branchId || !registration || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getGarageHiveOptions(garageId, {
        branchId,
        vehicleRegistration: registration,
        serviceIds: [],
      });
      setBranches(config.branches);
      setVehicle(result.vehicle, { found: true });
      setVehicleRegistration(result.vehicle.registration);
      setAvailableServices(result.services);
      saveLead("location");
      onNext();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Garage Hive couldn't load this booking.",
      );
    } finally {
      setLoading(false);
    }
  }

  if (!config.sandbox) {
    return (
      <div>
        <StepHeader
          kicker="Choose a garage"
          title="Where would you like to book?"
          subtitle="Locations are provided live by Garage Hive."
          display={false}
        />
        <div
          className={s.branchList}
          role="radiogroup"
          aria-label="Garage Hive location"
        >
          {config.branches.map((branch) => {
            const selected = branch.id === branchId;
            return (
              <button
                key={branch.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={[
                  s.branchItem,
                  selected ? s.branchSelected : "",
                ].join(" ")}
                onClick={() => setBranchId(branch.id)}
              >
                <span className={s.branchRadio} aria-hidden="true" />
                <span className={s.branchBody}>
                  <span className={s.branchName}>{branch.name}</span>
                  <span className={s.branchAddr}>{branch.addressLine}</span>
                </span>
              </button>
            );
          })}
        </div>
        {error && (
          <p
            role="alert"
            style={{ color: "var(--ap-danger)", fontSize: 13, fontWeight: 600 }}
          >
            {error}
          </p>
        )}
        <div className={s.actions}>
          <Button
            block
            loading={loading}
            disabled={!branchId}
            onClick={() => void continueWithGarageHive()}
            trailingIcon={<ArrowRight size={18} />}
          >
            {config.copy.ctaContinue}
          </Button>
        </div>
      </div>
    );
  }

  async function resolveAndContinue(
    pc: string | null,
    coords?: { lat: number; lng: number },
  ) {
    setLoading(true);
    setError(null);
    try {
      const branches = await api.getBranches(garageId, pc ?? undefined);
      // If we have coords but no postcode, still sort client-side is handled server-side
      // for postcode; branches arrive sorted by distance when a postcode is given.
      void coords;
      setBranches(branches);
      setPostcode(pc);
      track("location_entered", { postcode: pc ?? null, usedGeolocation: !pc });
      saveLead("location");
      onNext();
    } catch {
      setError("We couldn't find branches near you. Try a different postcode.");
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!valid || loading) return;
    void resolveAndContinue(postcode.trim().toUpperCase());
  }

  function useMyLocation() {
    if (!navigator.geolocation) {
      setError("Location isn't available — please enter your postcode.");
      return;
    }
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        void resolveAndContinue(null, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        setLocating(false);
        setError("We couldn't get your location — please enter your postcode.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <StepHeader
        kicker="Where are you?"
        title="Find your nearest branch"
        subtitle="We'll show you the closest options and live availability."
        display={false}
      />
      <div className={s.stack}>
        <TextField
          label={config.copy.postcodeLabel}
          placeholder="e.g. CV1 2AB"
          value={postcode}
          onChange={(e) => setLocal(e.target.value.toUpperCase())}
          error={touched && !valid ? "Enter a valid UK postcode" : error}
          icon={<MapPin size={20} />}
          autoComplete="postal-code"
          enterKeyHint="go"
        />

        <div className={s.orRule}>or</div>

        <Button
          type="button"
          variant="secondary"
          block
          loading={locating}
          onClick={useMyLocation}
          leadingIcon={<Navigation size={17} />}
        >
          {config.copy.useLocationLabel}
        </Button>
      </div>

      <div className={s.actions}>
        <Button
          type="submit"
          block
          loading={loading}
          disabled={!valid}
          trailingIcon={<ArrowRight size={18} />}
        >
          {config.copy.ctaContinue}
        </Button>
      </div>
    </form>
  );
}
